/* @flow */
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import axios from 'axios';
import getConfigApps from '../config';
import {CHECK_HAS_THOR, UPDATE_ENTITLED_PRODUCTS, UPDATE_PRODUCTS_POST_THOR_CHECK, FETCH_APPS_CCXI_DATA, FETCH_PDP_CCXI_DATA} from '../types';
import appsUtil from '../utils/AppsUtil';
import RestApiUtil from '../utils/RestApiUtil';
import {isTestExperience} from '../utils/TestUtil';
import ThorConnectionUtil from '../utils/ThorConnectionUtil';
import {planProductsMap, productIdToSapCode, appButtonConfig, SophiaLocaleMap, SOPHIA_SURFACE_ID, CVSContentfulLocaleMap, campaignId} from '../constants/appsConstants';
import {getContentfulPath} from '../utils/contentfulDataPathUtil';
import {getAEMPath} from '../utils/aemPathUtil';
import {getCatalogBannersData} from './CatalogBannersActions';
import {KEYS_FOR_SERVICES} from '../utils/postIMSFetchAppsData';
import ContentParser from '@ccxi/ccxi-apps/src/utils/contentParser';

let sophiaPdpEndPointPromise = null;

function preProcessThorAndFfcData(ffcResponse, hasThor, ffcFallback, fallbackProductsJson, dispatch) {
	let ffcData = ffcResponse;
	let isThor = hasThor;
	if (ffcFallback && fallbackProductsJson) {
		appsUtil.processAndStoreFFCFallbackProducts(fallbackProductsJson);
	}
	if (!ffcData) {
		appsUtil.isFFCFallback = true;
		if (ffcFallback && fallbackProductsJson) {
			ffcData = ffcFallback;
			appsUtil.processAndStoreFFCFallbackProducts(fallbackProductsJson);
			dispatch({
				type: CHECK_HAS_THOR,
				payload: {hasThor: false}
			});
			isThor = false;
		}
	}
	else {
		appsUtil.isFFCFallback = false;
	}
	return {ffcData, isThor};
}

function getFFCProductPromise() {
	const configObject = {
		channel: 'ccm,services,mobileApps',
		headers: {
			withAuth: true,
			withAppId: true
		},
		config: {
			allVersion: false
		},
		isCCD: false
	};

	return RestApiUtil.getFFCProductPromise(configObject).catch(useNull);
}

function initiateCommonAppsDataRequests(ffcCVSEntitlementDataPromiseArr, starterPlan, preFetchedData: Function = () => null) {
	const thorDetectPromise = [
		preFetchedData(KEYS_FOR_SERVICES.CHECK_FOR_THOR) || ThorConnectionUtil.checkForThor().catch(useNull)
	];

	const skeletonFallbackUrl = isTestExperience(campaignId.categoryPageRevampTest)
		? '/assets/apps/apps-skeleton/en/appsSkeletonRevamp.json'
		: '/assets/apps/apps-skeleton/en/appsSkeleton.json';
	ffcCVSEntitlementDataPromiseArr.push(
		preFetchedData(KEYS_FOR_SERVICES.FFC_LATEST) || getFFCProductPromise(),
		preFetchedData(KEYS_FOR_SERVICES.FFC_LATEST_FALLBACK) || RestApiUtil.getFFCFallbackProductPromise('latest', true).catch(useNull),
		preFetchedData(KEYS_FOR_SERVICES.FFC_FALLBACK_PRODUCT_JSON) || RestApiUtil.fetchFFCFallbackProductsJson(), // needed to make download urls in case of fallback
		preFetchedData(KEYS_FOR_SERVICES.APP_SKELETON_FALLBACK) || axios.get(skeletonFallbackUrl).catch(useNull)
	);

	let allPromisesArray = ffcCVSEntitlementDataPromiseArr;
	const ffcAllVersionsDataPromiseArr = [];

	if (starterPlan && planProductsMap[starterPlan]) {
		const sapCodeKeys = Object.keys(planProductsMap[starterPlan]).filter(keyName => keyName.includes('ProductSapCode'));
		for (let i = 0; i < sapCodeKeys.length; i++) {
			const sapcode = planProductsMap[starterPlan][sapCodeKeys[i]];
			ffcAllVersionsDataPromiseArr.push(
				preFetchedData(`${KEYS_FOR_SERVICES.ALL_VERSION_FFC_DATA}-${sapcode}`) || RestApiUtil.fetchFFCAllVersionData(sapcode)
			);
		}
		if (starterPlan === 'ccpp' || starterPlan === 'ccppn') {
			ffcAllVersionsDataPromiseArr.push(
				preFetchedData(`${KEYS_FOR_SERVICES.ALL_VERSION_FFC_DATA}-PHSP`) || RestApiUtil.fetchFFCAllVersionData('PHSP')
			); // add phsp in case of activation test.
		}
	}
	if (starterPlan && productIdToSapCode[starterPlan] && appButtonConfig[productIdToSapCode[starterPlan]] && appButtonConfig[productIdToSapCode[starterPlan]].inPlan.pdp.indexOf('install') > -1) {
		const sapcode = productIdToSapCode[starterPlan];
		ffcAllVersionsDataPromiseArr.push(
			preFetchedData(`${KEYS_FOR_SERVICES.ALL_VERSION_FFC_DATA}-${sapcode}`) || RestApiUtil.fetchFFCAllVersionData(sapcode)
		);
	}
	if (!appsUtil.isMobileOrTab()) {
		allPromisesArray = allPromisesArray.concat(thorDetectPromise, ffcAllVersionsDataPromiseArr);
	}
	return allPromisesArray;
}

function getContentfulEndpoint(sapcode, inputLocale) {
	if (appsUtil.hasFeature('temp_test_render_sophia_latency') && appsUtil.sophiaPpdEndPoint && appsUtil.sophiaPpdEndPoint[sapcode]) {
		return appsUtil.sophiaPpdEndPoint[sapcode].replace('{{locale}}',CVSContentfulLocaleMap[inputLocale]);
	}
	else if (appsUtil.hasFeature('temp_contentful_to_aem_migration')) {
		const aemURl = getAEMPath(sapcode,inputLocale);
		return `${appsUtil.getAEMDomain()}${aemURl}`;
	}
	else if (!appsUtil.hasFeature('temp_test_render_sophia_latency') || sapcode === 'skeletonwithstock') { // fallback for app catalog
		const contentfulURl = getContentfulPath(sapcode,inputLocale);
		return `${appsUtil.getCVSDomain()}${contentfulURl}`;
	}
	return '';
}

function useNull(e) {
	console.log(e);
	return null;
}

function getEndPointsFromSophia(locale) {
	const servers = getConfigApps('servers');
	const sophiaEndpoint = `${servers.SOPHIA}?surfaceId=${SOPHIA_SURFACE_ID.appSkeleton}&surfaceId=${SOPHIA_SURFACE_ID.allAppsBanners}&surfaceId=${SOPHIA_SURFACE_ID.categoriesBanners}&surfaceId=${SOPHIA_SURFACE_ID.pdpCatalog}&productLanguage=${locale}`;
	return axios.get(
		sophiaEndpoint,
		{headers: RestApiUtil.getHeaders(true, false)}
	).catch(e => e);
};

export const updateEntitledProducts = () => async(dispatch: Function) => {
	dispatch({
		type: UPDATE_ENTITLED_PRODUCTS
	});
};


export const updateProductsPostThorCheck = () => async(dispatch: Function) => {
	dispatch({
		type: UPDATE_PRODUCTS_POST_THOR_CHECK
	});
};

export const getAppsDataForCCXUIWithSophiaIntegration = (inputLocale: string, imsData: Object, planCode: string, starterPlan: string, preFetchedData?: Function = () => null) => async(dispatch: Function) => {
	appsUtil.setImsData(imsData);
	const contentfulLocal = SophiaLocaleMap[inputLocale];
	const ffcCVSEntitlementDataPromiseArr = [
		preFetchedData(KEYS_FOR_SERVICES.GET_SOPHIA_ENDPOINTS) || getEndPointsFromSophia(contentfulLocal),
	];

	const allPromisesArray = initiateCommonAppsDataRequests(ffcCVSEntitlementDataPromiseArr, starterPlan, preFetchedData);
	return Promise.all(allPromisesArray).then(axios.spread((dataFromSophia, ffcResponse, ffcFallback, fallbackProductsJson, appsSkeletonFallback, hasThor) => {
		const {isThor, ffcData} = preProcessThorAndFfcData(ffcResponse, hasThor, ffcFallback, fallbackProductsJson, dispatch);

		// Fetching Url from common sophia call
		const sophiaEndPoints = appsUtil.fetchURLFromSophiaData(dataFromSophia,
			[
				SOPHIA_SURFACE_ID.allAppsBanners,
				SOPHIA_SURFACE_ID.appSkeleton,
				SOPHIA_SURFACE_ID.categoriesBanners,
				SOPHIA_SURFACE_ID.pdpCatalog
			]
		);

		const catalogBannerDataPromise = dispatch(getCatalogBannersData(sophiaEndPoints[SOPHIA_SURFACE_ID.allAppsBanners], inputLocale));
		getPDPCatalogEndPoint(sophiaEndPoints[SOPHIA_SURFACE_ID.pdpCatalog]);

		let appSkeletonPromise = preFetchedData(KEYS_FOR_SERVICES.APP_SKELETON_SOPHIA);
		if (!appSkeletonPromise) {
			let skeletonUrl = sophiaEndPoints[SOPHIA_SURFACE_ID.appSkeleton];
			skeletonUrl = skeletonUrl || getContentfulEndpoint('skeletonwithstock',inputLocale);
			appSkeletonPromise = axios.get(skeletonUrl);
		}

		return Promise.all([catalogBannerDataPromise, appSkeletonPromise
			.then((appsPanelSkeletonConfig) => {
				dispatch({
					type: FETCH_APPS_CCXI_DATA,
					payload: {appsPanelSkeletonConfig, ffcData, inputLocale, hasThor: isThor, planCode, appsSkeletonFallback}
				});
			}).
			catch(useNull)
		]);
	}));
};

export const getAppsDataForCCXUI = (inputLocale: string, imsData: Object, planCode: string, starterPlan: string, preFetchedData: Function = () => null) => async(dispatch: Function) => {
	appsUtil.setImsData(imsData);
	const contentfulSkeletonURL = getContentfulEndpoint('skeletonwithstock', inputLocale);
	const ffcCVSEntitlementDataPromiseArr = [
		preFetchedData(KEYS_FOR_SERVICES.APP_SKELETON) || axios.get(contentfulSkeletonURL, {timeout: 5000}).catch(useNull)
	];
	const allPromisesArray = initiateCommonAppsDataRequests(ffcCVSEntitlementDataPromiseArr, starterPlan, preFetchedData);

	Promise.all(allPromisesArray).then(axios.spread((appsPanelSkeletonConfig, ffcResponse, ffcFallback, fallbackProductsJson, appsSkeletonFallback, hasThor) => {
		const {isThor, ffcData} = preProcessThorAndFfcData(ffcResponse, hasThor, ffcFallback, fallbackProductsJson, dispatch);
		dispatch({
			type: FETCH_APPS_CCXI_DATA,
			payload: {appsPanelSkeletonConfig, ffcData, inputLocale, hasThor: isThor, planCode, appsSkeletonFallback}
		});
	}));
};

export const setThor = (hasThor: boolean) => async(dispatch: Function) => {
	let isThor = hasThor;
	if (appsUtil.isFFCFallback) {
		isThor = false;
	}

	dispatch({
		type: CHECK_HAS_THOR,
		payload: {hasThor: isThor}
	});
};

export const checkThor = (preFetchedData?: Function = () => null) => async(dispatch: Function) => {
	if (appsUtil.isFFCFallback) {
		dispatch({
			type: CHECK_HAS_THOR,
			payload: {hasThor: false}
		});
	}
	else {
		const checkThorPromise = preFetchedData(KEYS_FOR_SERVICES.CHECK_FOR_THOR)
			|| ThorConnectionUtil.checkForThor();
		return checkThorPromise.then((hasThor) => {
			dispatch({
				type: CHECK_HAS_THOR,
				payload: {hasThor}
			});
		});
	}
};

const fetchPdpDataCCXI = (inputLocale: string, hasThor: boolean , sapCode: string, resolve: Function, reject: Function) => async(dispatch: Function) => {
	const allPromisesArray = [
		axios.get(getContentfulEndpoint(sapCode, inputLocale), {timeout: 10000}).catch(useNull)
	];
	if (!appsUtil.allVersionsFFCProductList[sapCode] && sapCode !== 'KCCC' && !appsUtil.isMobileOrTab()) {
		// fetch all version data for compatibility.
		if (!appsUtil.isFFCFallback) {
			allPromisesArray.push(RestApiUtil.fetchFFCAllVersionData(sapCode));
		}
		else {
			allPromisesArray.push(RestApiUtil.fetchFFCAllVersionDataFallback(sapCode));
		}
	}
	return Promise.all(allPromisesArray).then(axios.spread((PDPJson) => {
		if (PDPJson === null) {
			reject(false);
			return false;
		}
		dispatch({
			type: FETCH_PDP_CCXI_DATA,
			payload: {PDPJson, inputLocale, hasThor, sapCode, resolve, reject}
		});
	}));
};

export const getPDPDataForCCXUI = (inputLocale: string, hasThor: boolean , sapCode: string, resolve: Function, reject: Function) => async(dispatch: Function) => {
	if (appsUtil.hasFeature('temp_test_render_sophia_latency') && sophiaPdpEndPointPromise !== null) {
		return sophiaPdpEndPointPromise.then(() =>
			dispatch(fetchPdpDataCCXI(inputLocale, hasThor, sapCode, resolve, reject))
		);
	}
	return dispatch(fetchPdpDataCCXI(inputLocale, hasThor, sapCode, resolve, reject));
};

function getPDPCatalogEndPoint(cvsEndPoint: ?string) {
	sophiaPdpEndPointPromise = axios.get(cvsEndPoint).then((data) => {
		const parseData = data.data ? ContentParser.parse(data.data) : null;
		const pdpEndpoints = parseData && parseData.items && parseData.items[0].pdpUrl;
		appsUtil.sophiaPpdEndPoint = pdpEndpoints;
	}).
		catch(useNull);
}

export const __test__ = {
	setSophiaEndpointPromise: () => {sophiaPdpEndPointPromise = Promise.resolve('');},
	resetSophiaEndpointPromise: () => {sophiaPdpEndPointPromise = null;},
};
